import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, FolderOpen, Gem, Users, FileText, 
  TrendingUp, Settings, ChevronLeft, ChevronRight,
  Briefcase, Shield, Target, Kanban, ShieldCheck,
  Sparkles, Blocks, Eye, Globe, Send, SlidersHorizontal
} from 'lucide-react';
import { cn } from '@/lib/utils';

export function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['all'] },
    { icon: FolderOpen, label: 'Deals', path: '/deals', roles: ['all'] },
    { icon: Kanban, label: 'Pipeline', path: '/pipeline', roles: ['all'] },
    { icon: Gem, label: 'Minerals', path: '/minerals', roles: ['all'] },
    { icon: TrendingUp, label: 'Markets', path: '/markets', roles: ['all'] },
    { icon: Globe, label: 'Regional Map', path: '/regional-map', roles: ['all'] },
    { icon: Briefcase, label: 'Submit Deal', path: '/deals/submit', roles: ['all'] },
    { icon: Sparkles, label: 'AI Matching', path: '/ai-matching', roles: ['all'] },
    { icon: SlidersHorizontal, label: 'Preferences', path: '/preferences', roles: ['all'] },
    { icon: Target, label: 'Matching', path: '/matching', roles: ['admin', 'internal'] },
    { icon: Send, label: 'Send Match', path: '/send-match', roles: ['admin', 'internal'] },
    { icon: FileText, label: 'Data Room', path: '/data-room', roles: ['all'] },
    { icon: Users, label: 'CRM', path: '/crm', roles: ['admin', 'internal'] },
    { icon: TrendingUp, label: 'Commissions', path: '/commissions', roles: ['admin', 'internal'] },
    { icon: Shield, label: 'Access Requests', path: '/access-requests', roles: ['admin', 'internal'] },
    { icon: Eye, label: 'View Analytics', path: '/view-analytics', roles: ['admin', 'internal'] },
    { icon: Blocks, label: 'Digital Assets', path: '/digital-assets', roles: ['all'] },
    { icon: ShieldCheck, label: 'Admin', path: '/admin', roles: ['admin', 'internal'] },
    { icon: Settings, label: 'Settings', path: '/settings', roles: ['all'] }
  ];

  const filteredNavItems = navItems.filter(item => {
    if (item.roles.includes('all')) return true;
    return item.roles.includes(user?.role);
  });

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 z-40',
        collapsed ? 'w-16' : 'w-64'
      )}
      data-testid="sidebar"
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!collapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-accent rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">INMX</span>
              </div>
              <span className="font-bold text-sm">INMX</span>
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-1 hover:bg-muted rounded"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {filteredNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center space-x-3 px-3 py-2 rounded-sm text-sm transition-colors',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={18} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {!collapsed && user && (
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold">
                  {user.full_name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
